import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import WorkWithUs from "../components/sections/WorkWithUs";
import { launchBookingPayment } from "../utils/paymentLauncher";

const PayBooking = () => {
  const { encrypted_id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const decodedId = decodeURIComponent(encrypted_id);

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/pay-booking/${decodedId}`,
        );
        if (response.data.status) {
          setBooking(response.data.booking);
        } else {
          setError(response.data.message || "Invalid or expired payment link.");
        }
      } catch (err) {
        console.error("Error fetching booking details:", err);
        setError("Invalid or expired payment link.");
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  }, [decodedId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const normalized = String(timeStr).trim();
    const date = new Date(`1970-01-01T${normalized}`);
    if (Number.isNaN(date.getTime())) {
      return normalized;
    }
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handlePayment = async (payType = "full") => {
    if (!booking) return;
    try {
      await launchBookingPayment({ bookingId: booking.id, payType });
    } catch (error) {
      window.alert(error?.message || "Unable to start payment.");
    }
  };

  if (loading) {
    return (
      <>
        <section className="Booking_detail_wrap section_pb section_pt">
          <div className="container placeholder-glow">
            <div className="row justify-content-center">
              <div className="col-lg-8 col-md-10 col-12">
                <div className="bookingDetail_leftMain p-4 rounded shadow-sm">
                  <div
                    className="placeholder col-4 rounded mb-4"
                    style={{ height: "30px" }}
                  ></div>
                  <div className="detailCol_1">
                    <p
                      className="placeholder col-3 rounded"
                      style={{ height: "24px" }}
                    ></p>
                    <div className="text-end">
                    <div
                        className="placeholder col-2 rounded"
                        style={{ height: "32px" }}
                    ></div>
                    </div>
                  </div>
                  <hr />
                  <div className="typeCol_2">
                    <div
                      className="placeholder col-4 rounded mb-2"
                      style={{ height: "28px" }}
                    ></div>
                    <p
                      className="placeholder col-5 rounded mb-0"
                      style={{ height: "20px" }}
                    ></p>
                  </div>
                  <hr />
                  <div className="mt-4">
                    <div
                      className="placeholder col-12 rounded"
                      style={{ height: "50px" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <WorkWithUs />
      </>
    );
  }

  if (error || !booking) {
    return (
      <section className="section_pb section_pt text-center">
        <div className="container">
          <div className="alert alert-danger p-5 shadow-sm">
            <h4 className="mb-0">{error || "Booking not found"}</h4>
          </div>
        </div>
      </section>
    );
  }

  const firstRide =
    booking?.rides_data?.[0] ||
    booking?.rides?.[0] ||
    booking?.trips?.[0] ||
    {};
  const totalAmount = Number(booking?.total_customer_amount || 0);
  const paidAmount = Number(booking?.paid_amount || 0);
  const remainingAmount = Math.max(
    Number(booking?.remaining_amount ?? totalAmount - paidAmount),
    0,
  );
  const bookingReference =
    booking?.booking_code || booking?.request_id || `#${booking?.id || "-"}`;
  const isFullyPaid = remainingAmount <= 0;
  const paidPercent =
    totalAmount > 0 ? Math.min((paidAmount / totalAmount) * 100, 100) : 0;
  const payType = paidAmount > 0 ? "remaining" : "full";
  const primaryCtaLabel = isFullyPaid
    ? "Payment Complete"
    : `Pay Now (₹${formatCurrency(remainingAmount)})`;

  const displayDate = firstRide?.date || firstRide?.ride_date || "";
  const displayTime = firstRide?.time || firstRide?.pickup_time || "";
  const pickupLocation =
    firstRide?.pickup_location ||
    firstRide?.locations?.pickup_location ||
    "-";
  const dropLocation =
    firstRide?.drop_location ||
    firstRide?.locations?.drop_location ||
    "-";

  return (
    <>
      <section className="Booking_detail_wrap section_pb section_pt">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-9 col-lg-10 col-12">
              <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
                <div
                  className="px-4 px-md-5 py-4 py-md-5"
                  style={{ background: "linear-gradient(90deg, rgb(255, 247, 214) 0%, rgb(255, 239, 173) 100%)" }}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                      <p className="text-dark-50 mb-2 small text-uppercase fw-semibold">
                        Secure Payment Link
                      </p>
                      <h3 className="text-dark fw-bold mb-2">Complete Your Payment</h3>
                      <p className="text-dark-50 mb-0">
                        Booking <strong className="text-dark">{bookingReference}</strong>
                      </p>
                    </div>
                    <span
                      className={`badge px-3 py-2 ${
                        isFullyPaid
                          ? "bg-success-subtle text-dark border border-success-subtle"
                          : "bg-warning-subtle text-dark border border-warning-subtle"
                      }`}
                    >
                      {isFullyPaid ? "Paid" : "Pending"}
                    </span>
                  </div>
                </div>

                <div className="p-4 p-md-5">
                  <div className="row g-3 mb-4">
                    <div className="col-md-4 col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <small className="text-muted d-block mb-1">Total</small>
                        <h5 className="mb-0 fw-bold">₹{formatCurrency(totalAmount)}</h5>
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <small className="text-muted d-block mb-1">Paid</small>
                        <h5 className="mb-0 fw-bold text-success">
                          ₹{formatCurrency(paidAmount)}
                        </h5>
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div
                        className="rounded-3 p-3 h-100"
                        style={{
                          background: "linear-gradient(90deg, #fff7d6 0%, #ffefad 100%)",
                          border: "1px solid #ffe07a",
                        }}
                      >
                        <small className="text-muted d-block mb-1">Remaining</small>
                        <h5 className="mb-0 fw-bold text-dark">
                          ₹{formatCurrency(remainingAmount)}
                        </h5>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4">
                  <div className="col-lg-6 col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <h5 className="fw-bold mb-3">Trip Details</h5>
                        
                        <div className="py-2 border-bottom border-top">
                          <small className="text-muted d-block">Pickup</small>
                          <p className="mb-0">{pickupLocation}</p>
                        </div>
                        <div className="py-2">
                          <small className="text-muted d-block">Drop</small>
                          <p className="mb-0">{dropLocation}</p>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                          <span className="text-muted">Date & Time</span>
                          <span className="fw-medium">{formatDate(displayDate) || "-"}, {formatTime(displayTime) || "-"}{" "}{firstRide?.time_zone || ""}</span>
                        </div>
                        <div className="d-flex justify-content-between py-2">
                          <span className="text-muted">Vehicle</span>
                          <span className="fw-medium">
                            {firstRide?.vehicle_type_name || "Not available"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <div className="border rounded-3 p-3 h-100 bg-light-subtle d-flex flex-column">
                        <h5 className="fw-bold mb-3">Payment Progress</h5>
                        <div className="d-flex justify-content-between small text-muted mb-2">
                          <span>Completion</span>
                          <span>{Math.round(paidPercent)}%</span>
                        </div>
                        <div className="progress mb-3" style={{ height: 8 }}>
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{ width: `${paidPercent}%` }}
                            aria-valuenow={paidPercent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                        <p className="mb-0 text-muted small">
                          <i className="fa fa-lock me-2"></i>
                          You will be redirected to secure payment gateway.
                        </p>

                        <div className="mt-auto pt-3">
                          {!isFullyPaid ? (
                            <button
                              onClick={() => handlePayment(payType)}
                              className="btn btn-warning fw-bold py-2 w-100 border-0 shadow-sm text-dark rounded-pill"
                            >
                              {primaryCtaLabel}
                            </button>
                          ) : (
                            <div className="alert alert-success mb-0 py-2 text-center small">
                              <i className="fa fa-check-circle me-2"></i>
                              Payment Complete
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                  
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PayBooking;
